import { useRef, useState } from "react";
import { downloadExcel, uploadExcel } from "./excelService";
import styles from "./ImportExportBar.module.css";

/**
 * ImportExportBar
 *
 * Renderiza únicamente los 3 botones (Plantilla / Importar / Exportar).
 * El resultado de la importación y la lista de pendientes NO se renderizan
 * aquí — se pasan hacia arriba mediante el callback `onResult` para que
 * el padre los muestre a ancho completo donde quiera.
 *
 * Props:
 *   plantillaUrl, importarUrl, exportarUrl  — endpoints
 *   plantillaFilename, exportarFilename     — nombres de descarga
 *   onImportSuccess(result)                 — callback tras importación OK
 *   onResult(resultado, pendientes)         — callback con datos para mostrar
 *                                             resultado: { ok, msg, errores }
 *                                             pendientes: array (solo si isReceipts)
 *   isReceipts                              — activa lógica de pendientes
 */
export default function ImportExportBar({
  plantillaUrl,
  importarUrl,
  exportarUrl,
  plantillaFilename = "plantilla.xlsx",
  exportarFilename = "exportacion.xlsx",
  onImportSuccess,
  onResult, // ← nuevo: el padre recibe resultado + pendientes
  isReceipts = false,
}) {
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(null);

  async function handlePlantilla() {
    setLoading("plantilla");
    try {
      await downloadExcel(plantillaUrl, plantillaFilename);
    } catch (e) {
      onResult?.({ ok: false, msg: e.message, errores: [] }, []);
    } finally {
      setLoading(null);
    }
  }

  async function handleExportar() {
    setLoading("exportar");
    try {
      await downloadExcel(exportarUrl, exportarFilename);
    } catch (e) {
      onResult?.({ ok: false, msg: e.message, errores: [] }, []);
    } finally {
      setLoading(null);
    }
  }

  function handleImportarClick() {
    onResult?.(null, []); // limpiar resultado previo
    fileInputRef.current?.click();
  }

  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    setLoading("importar");
    onResult?.(null, []);

    try {
      const result = await uploadExcel(importarUrl, file);
      const total = result.creadas ?? result.creados ?? 0;
      const errores = result.errores ?? [];
      const pend = isReceipts ? (result.pendientes_de_aplicar ?? []) : [];

      const resultado = {
        ok: true,
        msg: `✅ ${total} registro(s) importado(s).${
          pend.length > 0
            ? ` ${pend.length} recibo(s) quedaron pendientes de aplicar.`
            : ""
        }`,
        errores,
      };

      onResult?.(resultado, pend);
      onImportSuccess?.(result);
    } catch (e) {
      onResult?.({ ok: false, msg: `❌ ${e.message}`, errores: [] }, []);
    } finally {
      setLoading(null);
    }
  }

  return (
    <div className={styles.btnGroup}>
      <button
        className={`${styles.btn} ${styles.btnPlantilla}`}
        onClick={handlePlantilla}
        disabled={!!loading}
        title="Descargar plantilla Excel vacía"
      >
        {loading === "plantilla" ? (
          <span className={styles.spinner} />
        ) : (
          <i className="fa-solid fa-file-arrow-down" />
        )}
        Plantilla
      </button>

      <button
        className={`${styles.btn} ${styles.btnImportar}`}
        onClick={handleImportarClick}
        disabled={!!loading}
        title="Importar datos desde Excel"
      >
        {loading === "importar" ? (
          <span className={styles.spinner} />
        ) : (
          <i className="fa-solid fa-file-import" />
        )}
        Importar
      </button>

      {exportarUrl && (
        <button
          className={`${styles.btn} ${styles.btnExportar}`}
          onClick={handleExportar}
          disabled={!!loading}
          title="Exportar datos actuales a Excel"
        >
          {loading === "exportar" ? (
            <span className={styles.spinner} />
          ) : (
            <i className="fa-solid fa-file-export" />
          )}
          Exportar
        </button>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
    </div>
  );
}
